import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { mediaVariantIds } from '../../utils/mediaUtils';
import { useSelector } from 'react-redux';
import ProductFilters from './ProductFilters';
import ProductListTable from './ProductListTable';
import ApprovalRowActions from './ApprovalRowActions';
import ReasonModal from './ReasonModal';
import ProductDetailView, { mapFormDataToApiPayload } from './ProductDetailView';
import ApprovalEditorView from './ApprovalEditorView';
import Pagination from './Pagination';
import { useProductsChildRoute } from './productsNavigation';
import {
  getCurrentUserRole,
  listProducts,
  getProductDetail,
  approveProduct,
  rejectProduct,
  requestChangesProduct,
  bulkApproveProducts,
  listCategories,
  listVendors,
  updateProduct,
  updateProductOptions,
} from '../../services/shipturtleApi';
import './ManageProducts.css';
import { useTaraTerminalFocus } from '../../utils/taraTerminalContext';

// Helper function to map API products to table format
const mapProductsForTable = (items) => {
  return (Array.isArray(items) ? items : []).map(p => {
    const firstVariant = Array.isArray(p.variants) && p.variants.length > 0 ? p.variants[0] : null;
    const rawMeta = p.meta || {};
    const filteredMeta = Object.fromEntries(
      Object.entries(rawMeta).filter(([k]) => !['vendor_remarks', 'draft_reason', 'tags', 'inventory', 'price'].includes(k))
    );

    return {
      id: p.id,
      title: p.title || '-',
      sku: firstVariant?.sku || '-',
      category: p.category || null,
      categoryId: p.category?.id || null,
      categoryName: p.category?.name || '',
      categorySlug: p.category?.slug || '',
      vendorId: p.vendor?.id || null,
      vendorName: p.vendor?.brand_name || p.vendor?.company_name || '-',
      vendor: p.vendor || null,
      // `state` and `terminal_status` are the real backend fields, passed through as-is.
      // `approval_status` is the one derived display field the "Status" column shows —
      // named apart from the two real fields so it can never be confused with, or
      // overwrite, either of them.
      approval_status: p.state === 'pending'
        ? 'pending_approval'
        : (p.terminal_status || p.state || '-'),
      state: p.state || 'pending',
      terminal_status: p.terminal_status || null,
      // Passed through so the list's Store / Mapping Status columns reflect real
      // mappings — without it every pending row would read "Unmapped".
      store_mappings: Array.isArray(p.store_mappings) ? p.store_mappings : [],
      inventory: firstVariant?.inventory_quantity ?? firstVariant?.meta?.inventory ?? 0,
      price: firstVariant?.price ?? 0,
      compareAtPrice: firstVariant?.compare_price ?? 0,
      weight: firstVariant?.weight_kg ?? '',
      dimensions: {
        length: firstVariant?.length_cm ?? '',
        width: firstVariant?.width_cm ?? '',
        height: firstVariant?.height_cm ?? ''
      },
      hsCode: firstVariant?.hs_code || '',
      brandCommission: firstVariant?.commission_pct ?? '',
      merchantSplit: p.merchant_split || '',
      tags: Array.isArray(p.meta?.tags) ? p.meta.tags : (Array.isArray(p.tags) ? p.tags : []),
      description: p.description || '',
      features: p.features || '',
      careInstructions: p.care_instructions || '',
      images: Array.isArray(p.media)
        ? p.media.map(m => ({
            id: m.id,
            url: m.url,
            alt: m.alt_text || m.alt,
            is_primary: m.is_primary || false,
            // Carry the FULL assignment set through: this normalized product is handed
            // to the detail/editor views, so dropping variant_ids here made a shared
            // image look unassigned before those screens ever saw it.
            variant_ids: mediaVariantIds(m),
            variant_id: mediaVariantIds(m)[0] || null
          }))
        : [],
      media: Array.isArray(p.media) ? p.media : [],
      vendorRemarks: p.meta?.vendor_remarks || p.vendor_remarks || '',
      metaFields: filteredMeta,
      meta: p.meta || {},
      variants: p.variants || [],
      variantId: firstVariant?.id || '',
      variantVersion: firstVariant?.version || 1,
      publishedAt: p.published_at || null,
      createdAt: p.created_at || '',
      updatedAt: p.updated_at || '',
      created_at: p.created_at || '',
      updated_at: p.updated_at || '',
      createdBy: p.created_by
        ? { id: p.created_by.id, name: p.created_by.name }
        : null,
      updatedBy: p.updated_by
        ? { id: p.updated_by.id, name: p.updated_by.name }
        : null,
      shopifyId: p.shopify_id || p.external_product_id || null,
      shopifyVariantId: firstVariant?.shopify_variant_id || firstVariant?.external_variant_id || null,
      version: p.version || 1,
      published_at: p.published_at || null,
      _apiProduct: p
    };
  });
};

function AdminApprovalQueue() {
  // The product under review lives in the URL: /products/pending/:productId
  const {
    childId: routeProductId,
    openChild: openProduct,
    closeChild: closeProduct,
    isCurrentChild,
  } = useProductsChildRoute('pending');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reasonModalData, setReasonModalData] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailViewMode, setDetailViewMode] = useState(null);
  const [showApprovalEditor, setShowApprovalEditor] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [activeTab, setActiveTab] = useState('new');
  useTaraTerminalFocus('approval_queue', {
    list: activeTab === 'new' ? 'new products' : 'changes to existing products',
    reviewing: selectedProduct ? {
      product_id: selectedProduct.id,
      title: selectedProduct.title,
      mode: showApprovalEditor ? 'approval editor' : detailViewMode,
    } : undefined,
  });

  // Bulk approve result modal
  const [showBulkApproveResultModal, setShowBulkApproveResultModal] = useState(false);
  const [bulkApproveResults, setBulkApproveResults] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    globalSearch: '',
    status: 'all',
    category: 'all',
    vendor: 'all',
    channel: 'all',
    inventory: { min: '', max: '' },
    dateRange: 'all',
    productStatus: 'all'
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const debounceTimerRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);

  // Independent pagination state per tab
  const [newPage, setNewPage] = useState(1);
  const [existingPage, setExistingPage] = useState(1);
  const [itemsPerPage] = useState(100);

  // Independent server pagination metadata per tab (holds total_count, total_pages)
  const [newPagination, setNewPagination] = useState(null);
  const [existingPagination, setExistingPagination] = useState(null);

  // Get user info from Redux
  const userTypeRaw = useSelector(state => state.user?.user_type);
  const userId = useSelector(state => state.user?.id || 'user1');

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const roleData = await getCurrentUserRole();
        console.log('User role from backend:', roleData);
        setUserRole(roleData.role);
      } catch (err) {
        console.error('Error fetching user role:', err);
        setUserRole('admin');
      }
    };
    fetchUserRole();
  }, [userTypeRaw]);

  // Fetch categories and vendors for filters
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const categoriesResponse = await listCategories();
        if (categoriesResponse && categoriesResponse.categories) {
          setCategories(categoriesResponse.categories);
        }
        if (userRole === 'admin') {
          const vendorsResponse = await listVendors();
          if (vendorsResponse && vendorsResponse.results) {
            setVendors(vendorsResponse.results);
          }
        }
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };
    if (userRole) {
      fetchFilterOptions();
    }
  }, [userRole]);

  // Debounce filter changes (500ms)
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
      // Reset both tab pages when filters change
      setNewPage(1);
      setExistingPage(1);
    }, 500);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // Build base API params from filters (shared across both tabs)
  const buildBaseApiParams = useCallback(() => {
    const apiParams = {
      status: 'pending_approval',
      limit: itemsPerPage,
    };

    if (debouncedFilters.category && debouncedFilters.category !== 'all') {
      apiParams.category_id = debouncedFilters.category;
    }
    if (debouncedFilters.vendor && debouncedFilters.vendor !== 'all') {
      apiParams.vendor_id = debouncedFilters.vendor;
    }
    if (debouncedFilters.globalSearch) {
      apiParams.search = debouncedFilters.globalSearch;
    }
    if (debouncedFilters.inventory?.min) {
      apiParams.inventory_min = debouncedFilters.inventory.min;
    }
    if (debouncedFilters.inventory?.max) {
      apiParams.inventory_max = debouncedFilters.inventory.max;
    }
    if (debouncedFilters.channel && debouncedFilters.channel !== 'all') {
      apiParams.channel = debouncedFilters.channel;
    }
    if (debouncedFilters.productStatus && debouncedFilters.productStatus !== 'all') {
      // Catalog status filter → backend `terminal_status` (the old `product_state` param
      // was silently ignored by the v2 list API).
      apiParams.terminal_status = debouncedFilters.productStatus;
    }

    // Date range
    if (debouncedFilters.dateRange && debouncedFilters.dateRange !== 'all') {
      const now = new Date();
      let dateFrom = null;
      switch (debouncedFilters.dateRange) {
        case 'today':
          dateFrom = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'week':
          dateFrom = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          dateFrom = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'quarter':
          dateFrom = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case 'year':
          dateFrom = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          break;
      }
      if (dateFrom) {
        apiParams.date_from = dateFrom.toISOString();
      }
    }

    return apiParams;
  }, [debouncedFilters, itemsPerPage]);

  // Fetch the active tab's products + the other tab's count (limit=1 for count-only)
  const loadProducts = useCallback(async () => {
    if (!userRole) return;
    setLoading(true);
    try {
      const baseParams = buildBaseApiParams();
      const currentTabPage = activeTab === 'new' ? newPage : existingPage;

      // Active tab: full page of products
      const activeTabParams = {
        ...baseParams,
        offset: (currentTabPage - 1) * itemsPerPage,
        published_at_isnull: activeTab === 'new' ? 'true' : 'false',
      };

      // Other tab: limit=1 just to get total_count cheaply
      const otherTabParams = {
        ...baseParams,
        limit: 1,
        offset: 0,
        published_at_isnull: activeTab === 'new' ? 'false' : 'true',
      };

      const [activeResponse, otherResponse] = await Promise.all([
        listProducts(activeTabParams),
        listProducts(otherTabParams),
      ]);

      // Set products for the active tab
      const { products: items = [], pagination: activePag } = activeResponse || {};
      setProducts(mapProductsForTable(items));

      // Store pagination metadata for both tabs
      if (activeTab === 'new') {
        setNewPagination(activePag || null);
        setExistingPagination(otherResponse?.pagination || null);
      } else {
        setExistingPagination(activePag || null);
        setNewPagination(otherResponse?.pagination || null);
      }
    } catch (error) {
      console.error('Error fetching pending products:', error);
    } finally {
      setLoading(false);
    }
  }, [userRole, buildBaseApiParams, activeTab, newPage, existingPage, itemsPerPage]);

  // Re-fetch when role, filters, tab or page change
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Clear selections when switching tabs
  useEffect(() => {
    setSelectedProducts([]);
  }, [activeTab]);

  // Derived pagination values
  const currentPage = activeTab === 'new' ? newPage : existingPage;
  const setCurrentPage = activeTab === 'new' ? setNewPage : setExistingPage;
  const activePagination = activeTab === 'new' ? newPagination : existingPagination;
  const totalPages = activePagination ? Number(activePagination.total_pages) || 1 : 1;
  const totalNewCount = newPagination ? Number(newPagination.total_count) || 0 : 0;
  const totalExistingCount = existingPagination ? Number(existingPagination.total_count) || 0 : 0;
  const totalPendingCount = totalNewCount + totalExistingCount;

  // Handle selections
  const handleProductSelect = (productId, isSelected) => {
    if (isSelected) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedProducts(products.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  // Bulk actions
  const handleBulkAction = (action) => {
    if (selectedProducts.length === 0) {
      alert('Please select at least one product');
      return;
    }
    switch (action) {
      case 'approve':
        handleBulkApprove();
        break;
      case 'reject':
        setReasonModalData({ action: 'reject', productIds: selectedProducts, isBulk: true });
        setShowReasonModal(true);
        break;
      case 'request_changes':
        setReasonModalData({ action: 'request_changes', productIds: selectedProducts, isBulk: true });
        setShowReasonModal(true);
        break;
      default:
        console.log('Unknown bulk action:', action);
    }
  };

  const handleBulkApprove = async () => {
    try {
      setLoading(true);
      const response = await bulkApproveProducts({ product_ids: selectedProducts, mode: 'queue' });
      await loadProducts();

      // Check for per-product sync errors in the response
      const syncErrors = response?.errors || [];
      if (syncErrors.length > 0) {
        setBulkApproveResults({
          total: selectedProducts.length,
          approved: response?.approved_count ?? (selectedProducts.length - syncErrors.length),
          errors: syncErrors
        });
        setShowBulkApproveResultModal(true);
      } else {
        alert(`Successfully approved ${selectedProducts.length} products`);
      }

      setSelectedProducts([]);
    } catch (error) {
      console.error('Error bulk approving products:', error);
      alert('Failed to approve products');
    } finally {
      setLoading(false);
    }
  };

  const handleProductAction = (action, productId, additionalData = {}) => {
    switch (action) {
      case 'approve':
        if (userRole === 'admin') {
          handleApprove(productId, 'push').then(response => {
            if (response?.shopify_sync_error) {
              alert('Product approved but Shopify sync failed \u2014 it will retry on next save.');
            } else {
              alert('Product approved successfully');
            }
          }).catch((error) => {
            const details = error?.response?.data?.details;
            const message = error?.response?.data?.error || 'Failed to approve product';
            if (details && details.length > 0) {
              alert(`${message}\n\n${details.join('\n')}`);
            } else {
              alert(message);
            }
          });
        }
        break;
      case 'request_changes':
        if (userRole === 'admin') {
          setReasonModalData({ action: 'request_changes', productIds: [productId], isBulk: false });
          setShowReasonModal(true);
        }
        break;
      case 'reject':
        if (userRole === 'admin') {
          setReasonModalData({ action: 'reject', productIds: [productId], isBulk: false });
          setShowReasonModal(true);
        }
        break;
      case 'review':
      case 'edit':
      case 'view':
        // Admins review in ApprovalEditorView; vendors edit in ProductDetailView
        // (changes stay pending_approval until an admin approves). Which one
        // opens is decided by showProduct once the URL changes.
        openProduct(productId);
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  // Loads and shows the product named in the URL — the review editor for
  // admins, the editable detail view for vendors.
  const showProduct = async (productId) => {
    const isAdmin = userRole === 'admin';
    const listed = isAdmin && products.find(p => p.id === productId);
    if (listed) {
      setSelectedProduct(listed);
      setDetailViewMode(null);
      setShowApprovalEditor(true);
      return;
    }

    try {
      setLoading(true);
      const response = await getProductDetail(productId);
      if (!isCurrentChild(productId)) return; // user moved on while it loaded
      if (response && response.product) {
        if (isAdmin) {
          setSelectedProduct(mapProductsForTable([response.product])[0]);
          setDetailViewMode(null);
          setShowApprovalEditor(true);
        } else {
          setSelectedProduct({
            ...response.product,
            missing_fields: response.missing_fields || []
          });
          setDetailViewMode('edit');
          setShowApprovalEditor(false);
        }
        // A previously-approved product keeps its published_at when it's edited back
        // to pending, so it lives on the "Existing" tab, not "New" — switch to
        // whichever tab actually holds it so opening a product never looks like it
        // "disappeared" just because the wrong tab was showing.
        setActiveTab(response.product.published_at ? 'existing' : 'new');
      } else {
        alert('Failed to load product details');
        closeProduct();
      }
    } catch (err) {
      if (!isCurrentChild(productId)) return;
      console.error('Error fetching product details:', err);
      alert('Failed to load product details. Please try again.');
      closeProduct();
    } finally {
      setLoading(false);
    }
  };

  // The review / edit view follows the URL: entering /pending/:id opens that
  // product (row click, redirect from Manage, deep link, Forward), leaving it
  // (Back, ×, Cancel, approve/reject) closes it and reveals the queue as it was.
  useEffect(() => {
    if (!routeProductId) {
      setSelectedProduct(null);
      setDetailViewMode(null);
      setShowApprovalEditor(false);
      return;
    }
    if (userRole) showProduct(routeProductId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeProductId, userRole]);

  // Persist vendor edits to a pending-approval product. The backend keeps the
  // status as pending_approval (see product_update) and stages the changes
  // locally — they are pushed to Shopify only when the admin approves.
  //
  // We override the reason passed by ProductDetailView because skipReasonModal
  // is true here — the user is never prompted, and ProductDetailView passes a
  // generic 'Variant updated' string. A consistent vendor-specific audit
  // string is more useful in the audit log.
  //
  // We also intentionally do NOT unmount the detail view from here. After
  // onSave resolves, ProductDetailView still calls setHasChanges/showToast
  // and schedules a 5s timer to hide the toast; tearing the view down
  // synchronously would skip that feedback entirely and leak the timer onto
  // an unmounted component. The user closes via Cancel when done.
  const handleSaveVendorEdit = async (updatedProductData) => {
    const auditReason = 'Vendor updated pending-approval product';

    try {
      setLoading(true);

      if (updatedProductData.options) {
        try {
          await updateProductOptions(updatedProductData.id, { options: updatedProductData.options });
        } catch (optErr) {
          console.warn('[AdminApprovalQueue] Options update failed (non-blocking):', optErr);
        }
      }

      const apiPayload = mapFormDataToApiPayload(updatedProductData, auditReason);
      const response = await updateProduct(updatedProductData.id, apiPayload);

      if (!response || !response.product) {
        throw new Error('Failed to update product');
      }

      await loadProducts();
    } catch (error) {
      console.error('Error saving vendor edit:', error);
      alert(`Error saving product: ${error.message || 'Please try again.'}`);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (productId, mode) => {
    try {
      setLoading(true);
      const response = await approveProduct(productId, { mode });
      await loadProducts();
      return response;
    } catch (error) {
      console.error('Error approving product:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleReasonSubmit = async (reason) => {
    if (!reasonModalData) return;
    const { action, productIds } = reasonModalData;
    try {
      setLoading(true);
      if (action === 'reject') {
        for (const productId of productIds) {
          await rejectProduct(productId, reason);
        }
      } else if (action === 'request_changes') {
        for (const productId of productIds) {
          await requestChangesProduct(productId, reason);
        }
      }
      await loadProducts();
      alert(`Successfully ${action.replace('_', ' ')} ${productIds.length} product(s)`);
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      alert(`Failed to ${action.replace('_', ' ')} products`);
    } finally {
      setLoading(false);
      setShowReasonModal(false);
      setReasonModalData(null);
      setSelectedProducts([]);
    }
  };

  const handleCloseReasonModal = () => {
    setShowReasonModal(false);
    setReasonModalData(null);
  };

  // × / Cancel / after approve-reject: back up to the queue
  const handleCloseApprovalEditor = () => closeProduct();

  const handleCloseDetailView = () => closeProduct();

  return (
    <div className="approvalQueueContainer">
      {/* Product Detail View (vendors edit their pending-approval products here) */}
      {selectedProduct && detailViewMode && (
        <ProductDetailView
          key={selectedProduct.id}
          product={selectedProduct}
          isReadOnly={false}
          onSave={handleSaveVendorEdit}
          onClose={handleCloseDetailView}
          missingFields={selectedProduct.missing_fields || []}
          hideApprovalButton={true}
          skipReasonModal={true}
          userRole={userRole}
        />
      )}

      <div className="header">
        <h1 className="title">
          {userRole === 'admin' ? 'Product Approval Queue' : 'Pending Approval Products'}
        </h1>
        <div className="headerInfo">
          <span className="roleBadge">
            Role: {userRole ? userRole.replace('_', ' ').toUpperCase() : 'Loading...'}
          </span>
          <span className="queueInfo">
            {totalPendingCount} products pending approval ({totalNewCount} new, {totalExistingCount} existing)
          </span>
        </div>
      </div>

      {/* Filters (status hidden since queue is always pending_approval) */}
      <ProductFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        isReadOnly={false}
        userRole={userRole}
        categories={categories}
        vendors={vendors}
        hideStatusFilter={true}
      />

      {/* Bulk Actions */}
      {userRole === 'admin' && selectedProducts.length > 0 && (
        <div className="bulkActionsBar">
          <div className="bulkActionsInfo">
            <span>{selectedProducts.length} product(s) selected</span>
          </div>
          <div className="bulkActionsButtons">
            <button onClick={() => handleBulkAction('approve')} className="bulkActionButton primary">
              Approve Selected
            </button>
            <button onClick={() => handleBulkAction('request_changes')} className="bulkActionButton secondary">
              Request Changes
            </button>
            <button onClick={() => handleBulkAction('reject')} className="bulkActionButton danger">
              Reject Selected
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="subNav">
        <button className={`tabButton ${activeTab === 'new' ? 'active' : ''}`} onClick={() => setActiveTab('new')}>
          New Products ({totalNewCount})
        </button>
        <button className={`tabButton ${activeTab === 'existing' ? 'active' : ''}`} onClick={() => setActiveTab('existing')}>
          Existing Products ({totalExistingCount})
        </button>
      </div>

      {/* Tab Content */}
      <div className="approvalSection" style={{ marginTop: '1rem' }}>
        {products.length > 0 ? (
            <ProductListTable
              products={products}
              onProductAction={handleProductAction}
              onRowClick={(productId) => handleProductAction(userRole === 'admin' ? 'review' : 'edit', productId)}
              onProductSelect={handleProductSelect}
              onSelectAll={(isSelected) => {
                if (isSelected) {
                  setSelectedProducts(prev => [...new Set([...prev, ...products.map(p => p.id)])]);
                } else {
                  setSelectedProducts(prev => prev.filter(id => !products.find(p => p.id === id)));
                }
              }}
              selectedProducts={selectedProducts}
              isReadOnly={userRole !== 'admin'}
              userRole={userRole}
              isLoading={loading}
              userId={userId}
              showBulkActions={userRole === 'admin'}
              rowActionsComponent={ApprovalRowActions}
            />
        ) : (
          <p className="noProducts">
            {loading
              ? 'Loading products...'
              : activeTab === 'new'
                ? 'No new products pending approval'
                : 'No existing products pending approval'}
          </p>
        )}
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && products.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Reason Modal */}
      {showReasonModal && reasonModalData && (
        <ReasonModal
          action={reasonModalData.action}
          productIds={reasonModalData.productIds}
          isBulk={reasonModalData.isBulk}
          onSubmit={handleReasonSubmit}
          onClose={handleCloseReasonModal}
        />
      )}

      {/* Approval Editor */}
      {showApprovalEditor && selectedProduct && (
        <ApprovalEditorView
          key={selectedProduct.id}
          product={selectedProduct}
          onClose={handleCloseApprovalEditor}
          onApprove={handleApprove}
          onReject={(productId, reason) => {
            rejectProduct(productId, reason).then(() => {
              alert('Product rejected successfully');
              loadProducts();
            }).catch(err => {
              console.error('Error rejecting product:', err);
              alert('Failed to reject product');
            });
          }}
          onRequestChanges={(productId, reason) => {
            requestChangesProduct(productId, reason).then(() => {
              alert('Changes requested successfully');
              loadProducts();
            }).catch(err => {
              console.error('Error requesting changes:', err);
              alert('Failed to request changes');
            });
          }}
        />
      )}

      {/* Bulk Approve Result Modal */}
      {showBulkApproveResultModal && bulkApproveResults && (
        <div className="modalOverlay">
          <div className="modalContent">
            <div className="modalHeader">
              <h3>Bulk Approve Results</h3>
              <button
                className="modalClose"
                onClick={() => { setShowBulkApproveResultModal(false); setBulkApproveResults(null); }}
              >
                ×
              </button>
            </div>
            <div className="modalBody">
              <p>
                <strong>{bulkApproveResults.approved}</strong> of{' '}
                <strong>{bulkApproveResults.total}</strong> products approved successfully.
              </p>
              {bulkApproveResults.errors.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#e65100', marginBottom: '0.5rem' }}>
                    ⚠️ Sync Warnings ({bulkApproveResults.errors.length})
                  </h4>
                  <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.75rem' }}>
                    These products were approved locally but may need attention for Shopify sync:
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, maxHeight: '300px', overflowY: 'auto' }}>
                    {bulkApproveResults.errors.map((error, index) => (
                      <li
                        key={index}
                        style={{
                          padding: '0.75rem',
                          marginBottom: '0.5rem',
                          backgroundColor: '#fff3e0',
                          border: '1px solid #ffe0b2',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          color: '#e65100'
                        }}
                      >
                        {typeof error === 'string' ? error : error.message || JSON.stringify(error)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="modalFooter">
              <button
                className="modalButton primary"
                onClick={() => { setShowBulkApproveResultModal(false); setBulkApproveResults(null); }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminApprovalQueue;